import { City, DeliverDetails, Island } from 'components/common/types'
import { CalculatedItems } from 'components/common/types/calculatePrice.types'
import {
  GET_LOCATIONS_REQUEST,
  GET_LOCATIONS_SUCCESS,
  GET_LOCATIONS_FAILURE,
  SET_USER_ADDRESS_REQUEST,
  SET_USER_ADDRESS_FAILURE,
  SET_DELIVER_DETAILS,
  CART_ORDER_REQUEST,
  CART_ORDER_SUCCESS,
  CART_ORDER_FAILURE,
  CART_ORDER_TOGGLE_LOADING,
  COUPON_DISCOUNT_REQUEST,
  COUPON_DISCOUNT_SUCCESS,
  COUPON_DISCOUNT_FAILURE,
  RESET_CART,
  RESET_TO_INITIAL_STATE,
  SET_POST_CODE_VALIDATION,
  SET_ORDER_LOADING,
  SET_CREDIT_CARD_ISO_CODE,
  CALCULATE_FEE_SUCCESS,
  CALCULATE_FEE_REQUEST,
  CALCULATE_FEE_FAILURE
} from '../constants'

type ReducerStateParameter = {
  isLoading: boolean
  data: any
  error: any
}

export type Locations = {
  islands: Island[]
  cities: City[]
}

type IOrderReducer = {
  locations: {
    isLoading: boolean
    data: Locations | null
    error: any
  }
  coordinateAddress: {
    isLoading: boolean
    error: any
  }
  deliverDetails: DeliverDetails
  order: ReducerStateParameter
  coupon: {
    isLoading: boolean
    error: any
    couponId: string
    validCoupon: boolean | null
    amount: number
  }
  calculatedItems: {
    isLoading: boolean
    error: any
    data: CalculatedItems | null
    calculatedTotalPrice: 0
  }
  creditCardIsoCode: string
  needPostCodeValidation: boolean
}

const initialState: IOrderReducer = {
  locations: {
    isLoading: false,
    data: null,
    error: null
  },
  coordinateAddress: {
    isLoading: false,
    error: null
  },
  deliverDetails: {
    name: '',
    additionalInfo: '',
    phone: '',
    deliveryNotes: '',
    tip: {
      isPercentage: false,
      amount: '0'
    },
    showPhoneNumberInputArea: false,
    serviceTime: null,
    orderNotes: '',
    finalDeliveryOption: null,
    deliveryPaceOptions: [],
    paymentType: 'credit',
    serviceFee: 0,
    deliveryType: null,
    totalPrice: 0,
    deliveryDate: '',
    totalTax: 0,
    phoneDialDetails: null,
    destinationAddress: {
      town: '',
      parish: '',
      parishTown: '',
      island: '',
      googleResponse: undefined,
      address: '',
      deliveryPlace: '',
      filteredParishes: [],
      streetDetails: '',
      latitude: undefined,
      longitude: undefined
    },
    pickupAddress: {
      town: '',
      parish: '',
      parishTown: '',
      island: '',
      googleResponse: undefined,
      address: '',
      deliveryPlace: '',
      filteredParishes: [],
      streetDetails: '',
      latitude: undefined,
      longitude: undefined
    }
  },
  calculatedItems: {
    isLoading: false,
    data: null,
    calculatedTotalPrice: 0,
    error: null
  },
  order: {
    data: [],
    isLoading: false,
    error: null
  },
  coupon: {
    isLoading: false,
    error: null,
    couponId: '',
    validCoupon: null,
    amount: 0
  },
  creditCardIsoCode: '',
  needPostCodeValidation: false
}

const OrderReducer = (
  state: IOrderReducer = initialState,
  action
): IOrderReducer => {
  switch (action.type) {
    case SET_POST_CODE_VALIDATION: {
      return {
        ...state,
        needPostCodeValidation: action.payload
      }
    }

    case SET_CREDIT_CARD_ISO_CODE:
      return {
        ...state,
        creditCardIsoCode: action.payload
      }

    case SET_DELIVER_DETAILS: {
      return {
        ...state,
        deliverDetails: {
          ...state.deliverDetails,
          ...action.payload
        }
      }
    }

    case RESET_CART: {
      return {
        ...initialState,
        deliverDetails: {
          ...initialState.deliverDetails,
          deliveryType: state.deliverDetails.deliveryType,
          deliveryDate: action.payload.deliveryDate,
          destinationAddress: action.payload.destinationAddress,
          serviceTime: action.payload.serviceTime
        }
      }
    }

    case RESET_TO_INITIAL_STATE: {
      return {
        ...initialState,
        deliverDetails: {
          ...initialState.deliverDetails,
          deliveryType: state.deliverDetails.deliveryType,
          deliveryDate: action.payload.deliveryDate,
          destinationAddress: action.payload.destinationAddress,
          serviceTime: action.payload.serviceTime
        }
      }
    }

    // ------------------- LOADING STATE starts ----------------------

    case SET_USER_ADDRESS_REQUEST:
      return {
        ...state,
        coordinateAddress: {
          ...state.coordinateAddress,
          isLoading: true
        }
      }

    case CART_ORDER_REQUEST: {
      return {
        ...state,
        order: {
          ...state.order,
          isLoading: true
        }
      }
    }

    case COUPON_DISCOUNT_REQUEST: {
      return {
        ...state,
        coupon: {
          ...state.coupon,
          isLoading: true
        }
      }
    }

    case GET_LOCATIONS_REQUEST: {
      return {
        ...state,
        locations: {
          ...state.locations,
          isLoading: true
        }
      }
    }

    case CART_ORDER_TOGGLE_LOADING: {
      return {
        ...state,
        order: {
          ...state.order,
          isLoading: !state.order.isLoading
        }
      }
    }

    case SET_ORDER_LOADING:
      return {
        ...state,
        order: {
          ...state.order,
          isLoading: action.payload
        }
      }

    case CALCULATE_FEE_REQUEST: {
      return {
        ...state,
        calculatedItems: {
          ...state.calculatedItems,
          isLoading: true
        }
      }
    }

    // ------------------- LOADING STATE ENDS ----------------------
    // ------------------- SUCCESS STATE STARTS ----------------------

    case CART_ORDER_SUCCESS: {
      return initialState
    }

    case GET_LOCATIONS_SUCCESS: {
      return {
        ...state,
        locations: {
          isLoading: false,
          data: action.payload,
          error: null
        }
      }
    }

    case CALCULATE_FEE_SUCCESS: {
      return {
        ...state,
        calculatedItems: {
          isLoading: false,
          data: action.payload.calculatedItems,
          calculatedTotalPrice: action.payload.calculatedTotalPrice,
          error: null
        }
      }
    }

    case COUPON_DISCOUNT_SUCCESS: {
      return {
        ...state,
        coupon: {
          isLoading: false,
          amount: action.payload.amount,
          error: null,
          couponId: action.payload.couponId,
          validCoupon: true
        }
      }
    }

    // ------------------- SUCCESS STATE ENDS ----------------------
    // ------------------- FAILURE STATE STARTS ----------------------

    case GET_LOCATIONS_FAILURE: {
      return {
        ...state,
        locations: {
          ...state.locations,
          error: action.payload,
          isLoading: false
        }
      }
    }

    case CART_ORDER_FAILURE:
      return {
        ...state,
        order: {
          ...state.order,
          isLoading: false,
          error: action.payload
        }
      }

    case COUPON_DISCOUNT_FAILURE:
      return {
        ...state,
        coupon: {
          isLoading: false,
          error: action.payload,
          amount: 0,
          couponId: '',
          validCoupon: false
        }
      }

    case SET_USER_ADDRESS_FAILURE:
      return {
        ...state,
        coordinateAddress: {
          isLoading: false,
          error: action.payload
        }
      }

    case CALCULATE_FEE_FAILURE: {
      return {
        ...state,
        calculatedItems: {
          ...state.calculatedItems,
          isLoading: false,
          error: action.payload
        }
      }
    }

    default:
      return state
  }
}

export default OrderReducer
